import type { JSX } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Trash2,
  CircleCheck,
  Info,
  CircleX,
  Loader2,
  Bell,
  Ban,
  TriangleAlert,
} from "lucide-react";
import { buttonVariants } from "../ui/button";
import ReactMarkdown from "react-markdown";
import { linkComponents } from "../universal/markdown";

export interface ConfirmModalProps {
  isOpen: boolean;
  text: string;
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
  img?:
    | "success"
    | "delete"
    | "info"
    | "error"
    | "notification"
    | "cancel"
    | "warning";
  cancelText: string;
  confirmText?: string;
  isLoading?: boolean;
  subtext?: string;
}

/**
 * ConfirmModal - A generic confirmation modal for delete prompts,
 * success messages, info popups, and more.
 *
 * Note: Uses ReactMarkdown to render the text content
 *
 * @param {ConfirmModalProps} props - Props for the ConfirmModal component.
 * @returns {JSX.Element}
 */
export default function ConfirmModal({
  isOpen,
  text,
  title,
  subtext,
  onCancel,
  onConfirm,
  img,
  cancelText,
  confirmText,
  isLoading,
}: ConfirmModalProps): JSX.Element {
  // Strip leading whitespace so indented server messages don't render as code blocks
  const bodyText = text.replace(/^[ \t]+/gm, "");

  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex justify-center text-xl">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-muted-foreground text-sm space-y-4 flex flex-col items-center">
              {img === "delete" && (
                <Trash2 className="w-16 h-16 text-destructive mt-2" />
              )}

              {img === "error" && (
                <CircleX className="w-16 h-16 text-destructive mt-2" />
              )}

              {img === "success" && (
                <CircleCheck className="w-16 h-16 text-success mt-2" />
              )}

              {img === "info" && <Info className="w-16 h-16 mt-2" />}

              {img === "notification" && (
                <Bell className="h-16 w-16 mt-2 text-primary" />
              )}

              {img === "cancel" && (
                <Ban className="h-16 w-16 mt-2 text-destructive" />
              )}

              {img === "warning" && (
                <TriangleAlert className="h-16 w-16 mt-2 text-warning" />
              )}

              <div className="max-w-none prose prose-sm prose-stone text-center [&_ul]:max-h-64 [&_ul]:overflow-y-auto [&_ul]:pr-2 [&_ul]:text-left [&_ol]:text-left">
                <ReactMarkdown components={linkComponents}>
                  {bodyText}
                </ReactMarkdown>
              </div>
              {subtext && (
                <span className="text-sm font-semibold text-center text-muted-foreground">
                  {subtext}
                </span>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex justify-center! gap-4">
          <AlertDialogCancel
            onClick={() => {
              onCancel();
            }}
          >
            {cancelText}
          </AlertDialogCancel>
          {confirmText && (
            <AlertDialogAction
              disabled={isLoading}
              className={
                img === "delete" || img === "cancel"
                  ? buttonVariants({ variant: "destructive" })
                  : undefined
              }
              onClick={() => {
                onConfirm();
              }}
            >
              {isLoading ? (
                <Loader2 className="animate-spin size-4" />
              ) : (
                confirmText
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
